import React, { useMemo, useState, useRef } from 'react';
import { useSelector, connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Grid } from '@material-ui/core';
import Message from '../../components/Message';
import MyProfileForm, { getProgress } from '../../components/MyProfileForm';
import callApi, { fetchConfig } from '../../actions/callApi';
import { getModuleNames } from '../../helpers/getters';
import { getEventtiaDateFormat, baseMoment } from '../../helpers/dates';
import INPUT_TYPES, { INTERNAL_IDS } from '../../helpers/constants';
// import { ATTENDANCE_MODE } from '../../helpers/constants';
import { defaultAttendeeFieldsValues } from '../../helpers/customFields';
// import parseCustomFields from '../../helpers/customFields';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import { SIZE_LIMIT_MB } from '../../helpers/files';
import { selectCurrentAttendee } from '../../helpers/selectors';
import SubpageTitle from '../../components/SubpageTitle';
import TabletExitButton from '../../components/TabletExitButton';
// import MyQrButton from '../../components/MyQrButton';
// import MyQrButtonMobile from '../../components/MyQrButtonMobile';
// eslint-disable-next-line max-len
// import moduleEnabledForCurrentAttendeeType from '../../helpers/moduleEnabledForCurrentAttendeeType';
import CartierSimplebar from '../../components/CartierSimplebar';
// import Button from '../../components/Button';

const {
  IMAGE,
  FILE,
  CITY,
  DATE_TIME,
  SELECT,
  CHECKBOX,
} = INPUT_TYPES;

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.darkGrey.main,
    fontWeight: 'bold',
    width: 785,
    maxWidth: 785,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  simpleBarNegativeMargin: {
    paddingLeft: theme.spacing(theme.subpageDrawer.paddings.desktop.horizontal),
    marginLeft: -theme.spacing(theme.subpageDrawer.paddings.desktop.horizontal),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(theme.subpageDrawer.paddings.mobile.horizontal),
      marginLeft: -theme.spacing(theme.subpageDrawer.paddings.mobile.horizontal),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  moduleName: {
    color: theme.palette.darkGrey.main,
  },
  fromWrapper: {
    maxWidth: 785,
    paddingBottom: theme.spacing(4),
  },
  message: {
    // marginTop: -theme.spacing(4),
    marginBottom: theme.spacing(2),
    maxWidth: 785,
  },
  profileHeaderHybridMobile: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  profileHeaderHybrid: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  profileHeader: {
    width: '100%',
    textAlign: 'center',
  },
  qrGridIem: {
    alignSelf: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 0),
      order: 3,
    },
    justifyItems: 'center',
  },
  profileFormHelperText: {
    fontSize: 14,
    fontStyle: 'italic',
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      width: '100%',
      marginLeft: -theme.spacing(),
    },
  },
  title: {
    marginBottom: -theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
    },
  },
  // yourAttendanceContainer: {
  //   display: 'flex',
  //   flexDirection: 'column',
  //   alignItems: 'center',
  //   marginBottom: theme.spacing(4),
  // },
  // titleYourAttendance: {
  //   fontSize: 32,
  //   fontFamily: 'Baskerville Old',
  //   color: theme.palette.darkGrey.main,
  // },
  // subtitleYourAttendance: {
  //   fontSize: 20,
  //   color: theme.palette.darkGrey.main,
  // },
  // buttonsContainer: {
  //   width: '100%',
  //   display: 'flex',
  //   justifyContent: 'center',
  //   gap: theme.spacing(4),
  //   marginTop: theme.spacing(2),
  //   [theme.breakpoints.down('sm')]: {
  //     flexDirection: 'column',
  //     gap: theme.spacing(),
  //     alignItems: 'center',
  //   },
  // },
  // attendanceButton: {
  //   padding: theme.spacing(0, 4),
  //   width: 360,
  // },
}));

const MyProfile = ({
  callApi: dispatchCallApi, attendeeTypeCustomFields, appSettings,
  // attendeeTypes, entities,
}) => {
  const classes = useStyles();
  const { t } = useTranslation(['myProfile', 'global']);
  const moduleTitle = getModuleNames(appSettings, 'AttendeeProfile');
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const currentAttendee = useSelector(selectCurrentAttendee);

  const containerRef = useRef(null);

  const [customErrors, setCustomErrors] = useState([]);
  const [updating, setUpdating] = useState(false);
  const defaultValues = useMemo(() => (
    defaultAttendeeFieldsValues(currentAttendee.fields, attendeeTypeCustomFields)
  ), []);

  const [message, setMessage] = useState();

  const getRequiredFieldErrorTemplate = (stringKey) => ({
    type: 'manual',
    name: `attendee[customFields].${stringKey}`,
    message: t('global:formError.requiredField'),
  });

  const countryFieldHasNewValue = (fieldValue) => (
    fieldValue && typeof fieldValue === 'string'
  );

  const fileFieldHasNewValue = (fieldValue) => (
    Array.isArray(fieldValue) && fieldValue[0] instanceof File
  );

  const onUpdateProfile = (values) => {
    const data = new FormData();
    const {
      attendee: {
        city_id: cityId,
        first_name: firstName,
        last_name: lastName,
        job_title: jobTitle,
        birthdate,
        alternative_email: alternativeEmail,
        company,
        telephone,
        email,
        headshot,
        customFields,
      },
    } = values;

    const progress = getProgress(values, currentAttendee, attendeeTypeCustomFields);

    if (progress === 100) data.append('attendee[custom_fields][profile_completed]', true);
    data.append('attendee[first_name]', firstName);
    data.append('attendee[last_name]', lastName);
    data.append('attendee[email]', email);
    if (company) data.append('attendee[company]', company);
    data.append('attendee[job_title]', jobTitle);
    data.append('attendee[telephone]', telephone);
    if (birthdate) data.append('attendee[birthdate]', birthdate);
    if (alternativeEmail) data.append('attendee[alternative_email]', alternativeEmail);

    if (countryFieldHasNewValue(cityId)) data.append('attendee[city_id]', cityId);
    if (fileFieldHasNewValue(headshot)) data.append('attendee[headshot]',
      headshot[0], headshot[0].name);

    const attendeeFieldValues = customFields || {};
    const fieldsErrors = [];

    Object.keys(attendeeFieldValues).forEach((stringKey) => {
      const key = stringKey.substring(1);
      const field = attendeeTypeCustomFields[key];
      let fieldValue = attendeeFieldValues[stringKey];
      if (
        [INTERNAL_IDS.arrivalTime, INTERNAL_IDS.departureTime].includes(field.internalId)
        && fieldValue
      ) fieldValue = baseMoment(fieldValue, 'HH:mm').format('h:mm A');
      const isEmpty = !fieldValue || (Array.isArray(fieldValue) && !fieldValue.length);
      const formDataKey = `attendee[custom_fields][${key}]`;
      const { inputType, includeTime } = field;

      switch (inputType) {
        case IMAGE:
        case FILE:
          if (fileFieldHasNewValue(fieldValue)) data.append(formDataKey, fieldValue[0],
            fieldValue[0].name); else if (field.required && isEmpty
              && !(fieldValue?.originalUrl)) fieldsErrors
            .push(getRequiredFieldErrorTemplate(stringKey));
          break;
        case CITY:
          if (fieldValue
            && typeof fieldValue === 'string') data.append(formDataKey, fieldValue);
          else if (fieldValue?.cityId) data.append(formDataKey, fieldValue.cityId);
          else if (field.required) fieldsErrors
            .push(getRequiredFieldErrorTemplate(stringKey));
          break;
        case DATE_TIME:
          if (field.required && isEmpty) fieldsErrors
            .push(getRequiredFieldErrorTemplate(stringKey));
          else if (!isEmpty) data.append(formDataKey, getEventtiaDateFormat(fieldValue,
            includeTime));
          break;
        default:
          if (field.required && isEmpty) fieldsErrors
            .push(getRequiredFieldErrorTemplate(stringKey));
          else if (isEmpty && [SELECT, CHECKBOX].includes(inputType)) data.append(formDataKey,
            '');
          else if (!isEmpty) data.append(formDataKey, fieldValue);
      }
    });
    setCustomErrors(fieldsErrors);

    let totalSize = 0;
    [...data.values()].forEach((item) => {
      if (typeof item === 'string') totalSize += item.length;
      else totalSize += item.size;
    });
    const overSizeLimit = totalSize > (SIZE_LIMIT_MB * 1024 * 1024);

    if (overSizeLimit) setMessage({
      type: 'error',
      text: t('overSizeLimit', { sizeLimit: SIZE_LIMIT_MB }),
    });
    else if (!fieldsErrors.length && !updating) {
      setUpdating(true);
      dispatchCallApi('updateCurrentAttendee', { id: currentAttendee.id, data })
        .then(({ type, error: errors }) => {
          setUpdating(false);
          const [, updateSuccess, updateFailure] = fetchConfig.updateCurrentAttendee.types;
          if (type === updateSuccess) setMessage({
            type: 'success',
            text: t('updateSuccess'),
          });
          if (type === updateFailure && Array.isArray(errors)) setMessage({
            type: 'error',
            text: errors.join(', '),
          });
        });
    }

    if (containerRef.current) containerRef.current.getScrollElement().scrollTo(0, 0);
  };

  // const showLeadsOptions = moduleEnabledForCurrentAttendeeType(entities, 'AttendeeLeads');

  // const { VIRTUAL } = ATTENDANCE_MODE;
  // const { attendanceMode } = attendeeTypes[currentAttendee.attendeeType.id];

  // const customFields = parseCustomFields(currentAttendee.fields, attendeeTypeCustomFields);

  return (
    <div className={classes.root}>
      <TabletExitButton />
      <CartierSimplebar rootRef={containerRef} className={classes.simpleBarNegativeMargin}>
        <div className={!mobile ? classes.header : undefined}>
          <Grid container justify={mobile ? 'center' : 'flex-end'}>
            <Grid item xs={12}>
              <SubpageTitle text={moduleTitle} />
            </Grid>
            <Grid item xs={12} md={4} className={classes.qrGridIem}>
              <div className={clsx(classes.profileHeaderHybrid,
                mobile && classes.profileHeaderHybridMobile)}
              >
                <div className={classes.profileHeader}>
                  {/* {(true || (attendanceMode !== VIRTUAL && showLeadsOptions)) && (
                    mobile ? (
                      <MyQrButtonMobile
                        attendeeInfo={{ ...customFields, uuid: currentAttendee.uuid }}
                      />
                    )
                      : (
                        <MyQrButton
                          attendeeInfo={{ ...customFields, uuid: currentAttendee.uuid }}
                        />
                      )
                  )} */}
                </div>
              </div>
            </Grid>
            {/* <Grid item xs={12}>
              <div className={classes.yourAttendanceContainer}>
                <Typography
                  className={classes.titleYourAttendance}
                  variant="subtitle1"
                >
                  Your Attendance
                </Typography>
                <Typography
                  className={classes.subtitleYourAttendance}
                  variant="subtitle1"
                >
                  Will you attend the Maison Committee?
                </Typography>
                <div className={classes.buttonsContainer}>
                  <Button
                    onClick={undefined}
                    className={classes.attendanceButton}
                  >
                    YES, I WILL BE PRESENT IN PERSON
                  </Button>
                  <Button
                    onClick={undefined}
                    variant="secondary"
                    className={classes.attendanceButton}
                  >
                    NO, I WILL FOLLOW REMOTELY
                  </Button>
                </div>
              </div>
            </Grid> */}
            <Grid item xs={12}>
              <Typography
                className={classes.profileFormHelperText}
                variant="subtitle1"
              >
                * These fields will be displayed in the &apos;Who’s Here ?&apos; section
                and will be visible to other attendees.
              </Typography>
            </Grid>
          </Grid>
        </div>
        {message?.text && (
          <Message
            className={classes.message}
            type={message?.type}
            onClose={() => setMessage()}
            message={message?.text}
          />
        )}
        <div className={classes.fromWrapper}>
          <MyProfileForm
            disabled={updating}
            customErrors={customErrors}
            defaultValues={defaultValues}
            onSubmit={onUpdateProfile}
          />
        </div>
      </CartierSimplebar>
    </div>
  );
};

MyProfile.propTypes = {
  callApi: PropTypes.func.isRequired,
  attendeeTypeCustomFields: PropTypes.objectOf(
    CustomPropTypes.attendeeTypeCustomField
  ),
  appSettings: CustomPropTypes.appSettings.isRequired,
  // attendeeTypes: PropTypes.objectOf(
  //   CustomPropTypes.attendeeType
  // ).isRequired,
  // entities: PropTypes.objectOf(PropTypes.shape({
  //   id: PropTypes.string,
  // })).isRequired,
};

MyProfile.defaultProps = {
  attendeeTypeCustomFields: {},
};

const mapStateToProps = ({
  entities,
  entities: {
    attendeeTypeCustomFields,
    appSettings,
    attendeeTypes,
  },
}) => ({
  entities,
  attendeeTypeCustomFields,
  appSettings,
  attendeeTypes,
});

export default connect(mapStateToProps, { callApi })(MyProfile);
